<template lang="pug">
base-section#privacy-policy(space='56')
  base-page-content
    .privacy-shell
      .privacy-intro
        base-display-title(:title='t("pages.privacy.legal")', size='compact', tone='accent')
        p.privacy-lead {{ t('pages.privacy.lead') }}
      .privacy-layout
        aside.privacy-aside
          section.privacy-panel
            h2 {{ t('pages.privacy.responsible.heading') }}
            p
              | {{ t('pages.privacy.responsible.organization') }}
              br
              | {{ t('pages.privacy.responsible.group') }}
              br
              | {{ t('pages.privacy.responsible.person') }}
              br
              | {{ t('pages.privacy.responsible.street') }}
              br
              | {{ t('pages.privacy.responsible.city') }}
              br
              a(href='tel:+49 261 / 287 2560') {{ t('pages.privacy.responsible.phone') }}
          section.privacy-panel
            h2 {{ t('pages.privacy.copyright.heading') }}
            p(v-for='paragraph in copyrightParagraphs.slice(0, 1)', :key='paragraph') {{ rt(paragraph) }}
            p
              | {{ t('pages.privacy.copyright.mailPrefix') }} 
              a(href='mailto:riehle@uni-koblenz.de') riehle@uni-koblenz.de
              | .
            p(v-for='paragraph in copyrightParagraphs.slice(1)', :key='paragraph') {{ rt(paragraph) }}
          section.privacy-panel
            h2 {{ t('pages.privacy.references.heading') }}
            p {{ t('pages.privacy.references.body') }}
          section.privacy-panel
            h2 {{ t('pages.privacy.pictureCredits.heading') }}
            p {{ t('pages.privacy.pictureCredits.body') }}
        article.privacy-content.eot-reading
          h2 {{ t('pages.privacy.guideline.heading') }}
          p {{ t('pages.privacy.guideline.intro') }}
          section(v-for='section in privacySections', :key='section.heading')
            h3 {{ rt(section.heading) }}
            template(v-for='(paragraph, index) in section.paragraphs', :key='`${section.heading}-${index}`')
              p {{ rt(paragraph) }}
              ul(v-if='index === 0 && section.list?.length')
                li(v-for='item in section.list', :key='item') {{ rt(item) }}
            p(v-if='section.link')
              a(:href='section.link') {{ section.link }}
</template>

<script setup lang="ts">
defineOptions({
  name: 'PrivacySection',
})

const { rt, t, tm } = useI18n()
const copyrightParagraphs = computed(
  () => tm('pages.privacy.copyright.paragraphs') as string[],
)
const privacySections = computed(
  () =>
    tm('pages.privacy.guideline.sections') as Array<{
      heading: string
      paragraphs: string[]
      list?: string[]
      link?: string
    }>,
)
</script>

<style scoped>
#privacy-policy {
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 192, 216, 0.08),
      transparent 28rem
    ),
    linear-gradient(180deg, var(--surface-soft) 0%, var(--surface-page) 100%);
}

.privacy-shell {
  display: grid;
  gap: 1.4rem;
}

.privacy-intro {
  display: grid;
  gap: 0.7rem;
  max-width: 42rem;
}

.privacy-lead {
  margin: 0;
  color: var(--color-text-muted);
}

.privacy-layout {
  display: grid;
  grid-template-columns: minmax(16rem, 22rem) minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}

.privacy-aside,
.privacy-content {
  display: grid;
  gap: 1rem;
}

.privacy-panel,
.privacy-content {
  padding: 1.2rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-card);
}

.privacy-panel h2,
.privacy-content h2 {
  margin: 0 0 0.8rem;
  font-size: 0.82rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.privacy-panel p:last-child,
.privacy-content p:last-child {
  margin-bottom: 0;
}

.privacy-content h3 {
  margin: 2rem 0 0.75rem;
  font-family: var(--font-sans);
  font-size: 1.2rem;
  line-height: 1.15;
}

.privacy-content ul {
  margin: 0 0 1.35rem 1.4rem;
}

@media (max-width: 960px) {
  .privacy-layout {
    grid-template-columns: 1fr;
  }
}
</style>
